/* ═══════════════════════════════════════════════════════════════
   The Designer — the live feed behind every Related sections block.

   One small store per open document. The Designer pushes the project's
   section list into it (and pushes again whenever a spec is added to or
   removed from the project); every Related sections node view subscribes
   and redraws, and the picker reads which block is open. Node views are
   plain DOM, so this is a plain subscribe/notify object, not React state —
   React reads it through useSyncExternalStore.
   ═══════════════════════════════════════════════════════════════ */

import type { SectionRef } from '../../shared/related-sections';

export interface RelatedSectionsSnapshot {
  /** 'loading' until the project's section list has been read once. */
  status: 'loading' | 'ready';
  /** This spec's candidates: the project's other live sections, bound order. */
  candidates: SectionRef[];
  /** May this user change the picks (the surface's edit gate). */
  canEdit: boolean;
  /** This project spec's own id — the scope a block's picks must match
   *  (`seededFor`) to read as seeded (shared/related-sections.ts). */
  key: string;
}

export interface RelatedSectionsOpen {
  blockId: string;
  anchor: HTMLElement;
  /** Move focus into the picker (keyboard open) — a click or a selection
   *  leaves focus in the document. */
  focus: boolean;
}

/** What a node view needs. */
export interface RelatedSectionsHost {
  get(): RelatedSectionsSnapshot;
  subscribe(fn: () => void): () => void;
  open(blockId: string, anchor: HTMLElement, focus: boolean): void;
}

export interface RelatedSectionsStore extends RelatedSectionsHost {
  set(next: RelatedSectionsSnapshot): void;
  getOpen(): RelatedSectionsOpen | null;
  close(): void;
}

export function createRelatedSectionsStore(): RelatedSectionsStore {
  let snap: RelatedSectionsSnapshot = {
    status: 'loading',
    candidates: [],
    canEdit: false,
    key: '',
  };
  let open: RelatedSectionsOpen | null = null;
  const subs = new Set<() => void>();
  const notify = () => subs.forEach((fn) => fn());
  return {
    get: () => snap,
    getOpen: () => open,
    subscribe(fn) {
      subs.add(fn);
      return () => {
        subs.delete(fn);
      };
    },
    set(next) {
      snap = next;
      notify();
    },
    open(blockId, anchor, focus) {
      // Re-opening the open block only ever upgrades to a keyboard focus.
      if (open && open.blockId === blockId && open.anchor === anchor && (open.focus || !focus)) {
        return;
      }
      open = { blockId, anchor, focus };
      notify();
    },
    close() {
      if (!open) return;
      open = null;
      notify();
    },
  };
}
